/**
 * Safe logging utility that handles cases where console methods are not available
 * This prevents errors in production environments where console methods might be stripped or overridden
 */

interface Logger {
  log: (message?: any, ...optionalParams: any[]) => void;
  warn: (message?: any, ...optionalParams: any[]) => void;
  error: (message?: any, ...optionalParams: any[]) => void;
  info: (message?: any, ...optionalParams: any[]) => void;
  debug: (message?: any, ...optionalParams: any[]) => void;
  group: (message?: any, ...optionalParams: any[]) => void;
  groupEnd: () => void;
  groupCollapsed: (message?: any, ...optionalParams: any[]) => void;
  table: (message?: any, ...optionalParams: any[]) => void;
}

/**
 * Safe console wrapper that checks if console methods exist before using them
 */
const createSafeLogger = (): Logger => {
  const safeLog = (method: keyof Console) => (message?: any, ...optionalParams: any[]) => {
    try {
      if (typeof console !== 'undefined' && console && typeof console[method] === 'function') {
        (console[method] as any)(message, ...optionalParams);
      }
    } catch (error) {
      // Silently fail if console is not available
      // In production environments, this prevents errors when console methods are stripped
    }
  };

  return {
    log: safeLog('log'),
    warn: safeLog('warn'),
    error: safeLog('error'),
    info: safeLog('info'),
    debug: safeLog('debug'),
    group: safeLog('group'),
    groupEnd: safeLog('groupEnd'),
    groupCollapsed: safeLog('groupCollapsed'),
    table: safeLog('table'),
  };
};

// Export the safe logger instance
export const logger = createSafeLogger();

// For backward compatibility, export individual methods
export const { log, warn, error, info, debug, group, groupEnd, groupCollapsed, table } = logger;

export default logger;